import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Tooltip, Typography } from '@material-ui/core'

// ----------------------------------------------------------------------
// FileNameText shows a whole file (or sample) name in monospace, wrapping it at natural
// break points (after `_ . - /`) instead of cutting it off. A name that needs more than
// `maxLines` lines that way but fits with a break inside a part is still shown whole
// (planLineBreaks); only a name too long for `maxLines` lines is middle-truncated, keeping the
// start and the distinguishing tail (e.g. `…_S3_L001_R1_001.fastq.gz`), with the full name in a
// tooltip. Monospace makes the maths exact: chars per line = floor(width / char width).
// ----------------------------------------------------------------------

export const ELLIPSIS = '…'

// A line may break after any of these (a <wbr/> is rendered after each one).
const BREAK_AFTER = new Set(['_', '.', '-', '/', ELLIPSIS])
// The tail kept by middleTruncate: at least this many characters...
const MIN_TAIL = 16
// ...as long as the start keeps at least this many.
const MIN_HEAD = 8
// Conservative slack so sub-pixel rounding never produces one more line than we planned.
const WIDTH_SLACK_PX = 0.5

const isSpace = (ch) => /\s/.test(ch || '')

const toText = (value) => (value === null || value === undefined ? '' : String(value))

// A name as an array of characters (code points), so an emoji or any other character outside
// the BMP is never cut in half.
const toChars = (value) => Array.from(toText(value))

// East Asian wide / fullwidth characters and emoji are not in the monospace font; the fallback
// font draws them about twice as wide, so they count as two columns (an upper bound).
const isWideCodePoint = (cp) =>
  (cp >= 0x1100 && cp <= 0x115f) ||
  (cp >= 0x2e80 && cp <= 0xa4cf && cp !== 0x303f) ||
  (cp >= 0xac00 && cp <= 0xd7a3) ||
  (cp >= 0xf900 && cp <= 0xfaff) ||
  (cp >= 0xfe30 && cp <= 0xfe4f) ||
  (cp >= 0xff00 && cp <= 0xff60) ||
  (cp >= 0xffe0 && cp <= 0xffe6) ||
  (cp >= 0x1f300 && cp <= 0x1faff) ||
  (cp >= 0x20000 && cp <= 0x3fffd)

const columnsOf = (text) => {
  let columns = 0
  Array.from(text).forEach((ch) => {
    columns += isWideCodePoint(ch.codePointAt(0)) ? 2 : 1
  })
  return columns
}

// Index (in `chars`) of the dot that starts a trailing extension (`.bam`, `.gz`), or -1. The
// extension stays on the same line as the part before it, so a name never ends with a lone
// `bam` line, and middleTruncate keeps it whenever there is room.
const extensionDotIndex = (chars) => {
  const dot = chars.lastIndexOf('.')
  if (dot <= 0 || dot === chars.length - 1) return -1
  for (let i = dot + 1; i < chars.length; i += 1) {
    if (BREAK_AFTER.has(chars[i]) || isSpace(chars[i])) return -1
  }
  return dot
}

/**
 * Split `text` into the pieces a browser may put on separate lines: each piece ends right after
 * a break character (`_ . - / …`) or after a run of whitespace. The dot of a trailing extension
 * is not a break, so the extension stays with the part before it (`sorted.bam`, not a lone
 * `bam` line), unless `charsPerLine` is given and that joined piece is wider than a line: then
 * it breaks after the dot (`deneme.` / `bam`) rather than inside the extension (`deneme.b`/`am`).
 */
export function splitAtBreaks(text, charsPerLine) {
  const chars = toChars(text)
  let extensionDot = extensionDotIndex(chars)
  if (extensionDot > 0 && charsPerLine > 0) {
    let start = extensionDot
    while (start > 0 && !BREAK_AFTER.has(chars[start - 1]) && !isSpace(chars[start - 1])) start -= 1
    if (columnsOf(chars.slice(start).join('')) > Math.floor(charsPerLine)) extensionDot = -1
  }
  const parts = []
  let current = ''
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i]
    current += ch
    if ((BREAK_AFTER.has(ch) && i !== extensionDot) || (isSpace(ch) && !isSpace(chars[i + 1]))) {
      parts.push(current)
      current = ''
    }
  }
  if (current) parts.push(current)
  return parts
}

/**
 * How many lines `text` takes in a box `charsPerLine` monospace characters wide, wrapping
 * greedily at the break points above and splitting a piece that cannot fit on a line of its
 * own anywhere (CSS `overflow-wrap: anywhere`). The browser may also break at a few extra
 * places (after `?`, `)`…), which can only save lines, so this is an upper bound.
 */
export function countLines(text, charsPerLine) {
  const source = toText(text)
  if (!source) return 0
  const cpl = Math.max(1, Math.floor(charsPerLine) || 1)
  let lines = 1
  let column = 0
  splitAtBreaks(source, cpl).forEach((part) => {
    // Trailing whitespace hangs past the line end, so it does not count when fitting.
    const trimmed = part.replace(/\s+$/, '')
    const visible = columnsOf(trimmed)
    const hanging = part.length - trimmed.length
    if (column > 0 && column + visible > cpl) {
      lines += 1
      column = 0
    }
    if (visible > cpl) {
      const extraLines = Math.ceil(visible / cpl) - 1
      lines += extraLines
      column = visible - extraLines * cpl + hanging
    } else {
      column += visible + hanging
    }
  })
  return lines
}

/**
 * Shorten `name` to at most `maxChars` characters by replacing its middle with a single "…".
 * Keeps the start and a tail of at least 16 characters or the full last two dot-segments
 * (`fastq.gz`, `aligned.bam`), whichever is longer, when that still leaves room for the start.
 * With less room the start keeps up to 8 characters and the tail the rest (at least half), still
 * keeping the whole extension when at least one character of the start fits beside it.
 * Lengths count characters (code points), so an emoji is never cut in half.
 */
export function middleTruncate(name, maxChars) {
  const chars = toChars(name)
  const limit = Math.floor(maxChars)
  if (!Number.isFinite(limit) || chars.length <= limit) return chars.join('')
  if (limit <= 0) return ''
  if (limit === 1) return ELLIPSIS

  const budget = limit - 1 // characters left for start + tail once the ellipsis is in
  const lastDot = chars.lastIndexOf('.')
  const prevDot = lastDot > 0 ? chars.lastIndexOf('.', lastDot - 1) : -1
  const dotTail = prevDot >= 0 ? chars.length - prevDot - 1 : 0
  const extensionDot = extensionDotIndex(chars)
  const extension = extensionDot > 0 ? chars.length - extensionDot : 0

  let tail = Math.max(MIN_TAIL, dotTail)
  if (budget - tail < MIN_HEAD) tail = MIN_TAIL
  if (budget - tail < MIN_HEAD) {
    // Too little room for both: the start keeps up to MIN_HEAD, the tail the rest (at least
    // half), so the tail shrinks smoothly from 16 instead of jumping to half the budget.
    tail = Math.max(Math.ceil(budget / 2), budget - MIN_HEAD)
    if (extension > tail && extension <= Math.min(MIN_TAIL, budget - 1)) tail = extension
  }
  const head = budget - tail

  return `${chars.slice(0, head).join('')}${ELLIPSIS}${chars.slice(chars.length - tail).join('')}`
}

// How much a line break just before `chars[i]` costs in planLineBreaks: nothing after a separator,
// a little before one or after the extension dot (a lone `bam` line), more inside a word and
// most inside the extension.
const breakCost = (chars, i, extensionDot) => {
  const before = chars[i - 1]
  if (i - 1 === extensionDot) return 1
  if (BREAK_AFTER.has(before) || isSpace(before)) return 0
  if (extensionDot >= 0 && i > extensionDot) return 6
  if (BREAK_AFTER.has(chars[i]) || isSpace(chars[i])) return 1
  return 3
}

/**
 * Line breaks (indices into the name's characters) that fit `text` into at most `maxLines` lines
 * of `charsPerLine` columns, allowing breaks inside a part but as few and as mild as possible
 * (see breakCost); null when it cannot fit even so. For a name that does not fit at its natural
 * break points but is short enough to show whole with one break inside a part.
 */
export function planLineBreaks(text, charsPerLine, maxLines = 3) {
  const chars = toChars(text)
  const n = chars.length
  const cpl = Math.floor(charsPerLine)
  if (!n || !(cpl > 0) || !(maxLines > 0)) return null
  const widths = chars.map((ch) => (isWideCodePoint(ch.codePointAt(0)) ? 2 : 1))
  if (widths.reduce((sum, width) => sum + width, 0) > cpl * maxLines) return null
  const extensionDot = extensionDotIndex(chars)

  // cost[j]: cheapest way to end a line right before chars[j] using the lines so far;
  // starts[k][j]: where that line began, to walk the plan back.
  let cost = new Array(n + 1).fill(Infinity)
  cost[0] = 0
  const starts = []
  let best = null
  for (let line = 1; line <= maxLines; line += 1) {
    const next = new Array(n + 1).fill(Infinity)
    const from = new Array(n + 1).fill(-1)
    for (let end = 1; end <= n; end += 1) {
      const endCost = end === n ? 0 : breakCost(chars, end, extensionDot)
      let columns = 0
      for (let start = end - 1; start >= 0; start -= 1) {
        columns += widths[start]
        if (columns > cpl) break
        if (cost[start] + endCost < next[end]) {
          next[end] = cost[start] + endCost
          from[end] = start
        }
      }
    }
    starts.push(from)
    if (next[n] < Infinity && (!best || next[n] < best.cost)) best = { cost: next[n], lines: line }
    cost = next
  }
  if (!best) return null

  const breaks = []
  let end = n
  for (let line = best.lines; line > 1; line -= 1) {
    end = starts[line - 1][end]
    breaks.unshift(end)
  }
  return breaks
}

/**
 * What to display for `name` in a box `charsPerLine` characters wide: the whole name when it
 * fits in `maxLines` lines at its natural break points; the whole name with planned `breaks`
 * (see planLineBreaks) when it only fits with a break inside a part; otherwise the longest
 * middle-truncation that fits at natural break points.
 */
export function fitFileName(name, charsPerLine, maxLines = 3) {
  const text = toText(name)
  const cpl = Math.floor(charsPerLine)
  if (!(cpl > 0) || !(maxLines > 0) || countLines(text, cpl) <= maxLines) {
    return { text, truncated: false }
  }
  const breaks = planLineBreaks(text, cpl, maxLines)
  if (breaks) return { text, truncated: false, breaks }
  const length = toChars(text).length
  for (let limit = Math.min(length - 1, cpl * maxLines); limit > 0; limit -= 1) {
    const candidate = middleTruncate(text, limit)
    if (countLines(candidate, cpl) <= maxLines) return { text: candidate, truncated: true }
  }
  return { text: ELLIPSIS, truncated: true }
}

// Pieces of `text` cut at `breaks` (character indices from planLineBreaks).
const splitAtIndices = (text, breaks) => {
  const chars = toChars(text)
  const parts = []
  let start = 0
  breaks.forEach((index) => {
    parts.push(chars.slice(start, index).join(''))
    start = index
  })
  parts.push(chars.slice(start).join(''))
  return parts
}

// ----------------------------------------------------------------------
// Measuring (browser only)

const MEASURE_SAMPLE = 'abcdefghijklmnopqrstuvwxyz0123456789_.-/'
const charWidthCache = new Map()
let measureContext = null

// Advance width of one character in the element's computed (monospace) font, measured once per
// font and cached. Falls back to 0.6em, the usual monospace advance, where canvas is missing.
function getCharWidth(style) {
  const fontSizePx = parseFloat(style.fontSize) || 13
  const letterSpacing = parseFloat(style.letterSpacing) || 0
  const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
  const key = `${font}|${letterSpacing}`
  if (charWidthCache.has(key)) return charWidthCache.get(key)

  let width = 0
  try {
    if (!measureContext) measureContext = document.createElement('canvas').getContext('2d')
    if (measureContext) {
      measureContext.font = font
      width = measureContext.measureText(MEASURE_SAMPLE).width / MEASURE_SAMPLE.length
    }
  } catch (error) {
    width = 0
  }
  if (!(width > 0)) width = fontSizePx * 0.6
  width += letterSpacing
  charWidthCache.set(key, width)
  return width
}

// Content-box width of `element`, the same box ResizeObserver's contentRect reports. Layout
// sizes (not getBoundingClientRect), so a running transform animation cannot skew it.
function contentWidth(element) {
  const style = window.getComputedStyle(element)
  return Math.max(0, element.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0))
}

// One ResizeObserver for every FileNameText on the page (a table page can hold hundreds).
let sharedObserver = null
const resizeCallbacks = new Map() // element -> Set of callbacks

function observeWidth(element, callback) {
  if (typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') return () => {}
  if (!sharedObserver) {
    sharedObserver = new window.ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const handlers = resizeCallbacks.get(entry.target)
        if (handlers) handlers.forEach((handler) => handler(entry.contentRect.width))
      })
    })
  }
  let handlers = resizeCallbacks.get(element)
  if (!handlers) {
    handlers = new Set()
    resizeCallbacks.set(element, handlers)
    sharedObserver.observe(element)
  }
  handlers.add(callback)
  return () => {
    handlers.delete(callback)
    if (handlers.size === 0 && resizeCallbacks.get(element) === handlers) {
      resizeCallbacks.delete(element)
      sharedObserver.unobserve(element)
    }
  }
}

/**
 * Width in px of `element` (a DOM node, e.g. kept from a callback ref), kept current through the
 * shared ResizeObserver; 0 until it has been measured.
 */
export function useElementWidth(element) {
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    if (!element) return undefined
    setWidth(Math.round(contentWidth(element)))
    let frame = null
    const stop = observeWidth(element, (next) => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        frame = null
        setWidth(Math.round(next))
      })
    })
    return () => {
      stop()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [element])
  return width
}

// Web fonts arrive after first paint: when they do, cached widths (measured with a fallback
// font) are dropped and every mounted instance re-measures.
let fontsVersion = 0
const fontListeners = new Set()
let fontsHooked = false

function hookFontLoading() {
  if (fontsHooked || typeof document === 'undefined' || !document.fonts) return
  fontsHooked = true
  const bump = () => {
    charWidthCache.clear()
    fontsVersion += 1
    fontListeners.forEach((listener) => listener(fontsVersion))
  }
  if (typeof document.fonts.addEventListener === 'function') document.fonts.addEventListener('loadingdone', bump)
  if (document.fonts.status !== 'loaded' && document.fonts.ready) document.fonts.ready.then(bump)
}

function useFontsVersion() {
  const [version, setVersion] = useState(fontsVersion)
  useEffect(() => {
    hookFontLoading()
    fontListeners.add(setVersion)
    if (version !== fontsVersion) setVersion(fontsVersion)
    return () => fontListeners.delete(setVersion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return version
}

// ----------------------------------------------------------------------

const ROOT_SX = {
  display: 'block',
  minWidth: 0,
  maxWidth: '100%',
  m: 0,
  fontFamily: 'fontFamilyMono',
  fontSize: '0.8125rem', // 13px
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: 0,
  color: 'text.primary',
}

const TEXT_SX = {
  display: 'inline-block',
  maxWidth: '100%',
  boxSizing: 'border-box',
  verticalAlign: 'top',
  whiteSpace: 'normal',
  // Break between name parts first (<wbr/>), anywhere only when a single part is wider than
  // the line. `word-break: break-word` is the same thing for Safari before 15.4.
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  userSelect: 'text',
  cursor: 'inherit',
}

const VARIANT_SX = {
  badge: {
    bgcolor: 'background.neutral',
    borderRadius: '4px',
    padding: '6px 12px',
  },
  plain: {},
}

// An invisible, zero-height copy of the whole name. It gives the box the min/max-content width
// of the full name whatever is displayed, so an auto-sized table column or a flex row sizes it
// as if the whole name were shown, and never narrower than the longest name part: it breaks
// only at the name's break points for sizing (the displayed text may still break anywhere).
// Without it, such a column can squeeze the name to one character a line ("P…z").
const SIZER_SX = {
  display: 'block',
  overflowWrap: 'break-word',
  wordBreak: 'normal',
  height: '0px', // a string: sx turns a bare 0 into "0%", which is auto in an auto-height box
  paddingTop: 0,
  paddingBottom: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  userSelect: 'none',
  pointerEvents: 'none',
}

const TOOLTIP_TEXT_SX = { fontFamily: 'fontFamilyMono', overflowWrap: 'anywhere', wordBreak: 'break-word' }

const renderParts = (parts) =>
  parts.map((part, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 ? <wbr /> : null}
    </React.Fragment>
  ))

const FileNameText = React.memo(({ name, maxLines = 3, variant = 'badge', sx }) => {
  const fullName = toText(name)
  const rootRef = useRef(null)
  const textRef = useRef(null)
  const [charsPerLine, setCharsPerLine] = useState(0)
  const fontsVersionSeen = useFontsVersion()

  const measure = useCallback((width) => {
    const textElement = textRef.current
    if (!textElement || !(width > 0)) return
    const style = window.getComputedStyle(textElement)
    const inset =
      (parseFloat(style.paddingLeft) || 0) +
      (parseFloat(style.paddingRight) || 0) +
      (parseFloat(style.borderLeftWidth) || 0) +
      (parseFloat(style.borderRightWidth) || 0)
    const next = Math.max(1, Math.floor((width - inset - WIDTH_SLACK_PX) / getCharWidth(style)))
    setCharsPerLine((previous) => (previous === next ? previous : next))
  }, [])

  // Measure before first paint, then follow the container's width. The observer callback is
  // deferred a frame so re-rendering (which changes this box's height) cannot trip the
  // "ResizeObserver loop" error.
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return undefined
    measure(contentWidth(root))
    let frame = null
    const stop = observeWidth(root, (width) => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        frame = null
        measure(width)
      })
    })
    return () => {
      stop()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [measure, variant, fontsVersionSeen])

  const { text, truncated, breaks } = useMemo(
    () => fitFileName(fullName, charsPerLine, maxLines),
    [fullName, charsPerLine, maxLines],
  )
  // A <wbr/> goes at every natural break point, or only at the planned ones when the name needs
  // a break inside a part to fit.
  const parts = useMemo(
    () => (breaks ? splitAtIndices(text, breaks) : splitAtBreaks(text, charsPerLine)),
    [text, breaks, charsPerLine],
  )

  // A plain click still reaches the row (selecting it); only a drag that selected part of the
  // name stops there, so copying a name does not also toggle the row.
  const handleClick = useCallback((event) => {
    const selection = typeof window.getSelection === 'function' ? window.getSelection() : null
    if (selection && !selection.isCollapsed && textRef.current && textRef.current.contains(selection.anchorNode)) {
      event.stopPropagation()
    }
  }, [])

  // Copying the whole shortened name copies the real name, not the "…" version.
  const handleCopy = useCallback(
    (event) => {
      if (!truncated || !event.clipboardData) return
      const selection = typeof window.getSelection === 'function' ? window.getSelection() : null
      if (selection && selection.toString().trim() === text) {
        event.clipboardData.setData('text/plain', fullName)
        event.preventDefault()
      }
    },
    [truncated, text, fullName],
  )

  const fullParts = useMemo(() => splitAtBreaks(fullName), [fullName])
  const variantSx = VARIANT_SX[variant] || VARIANT_SX.badge

  const textElement = (
    <Box
      component="span"
      ref={textRef}
      className="FileNameText-text"
      aria-label={truncated ? fullName : undefined}
      onClick={handleClick}
      onCopy={handleCopy}
      sx={{ ...TEXT_SX, ...variantSx }}
    >
      {renderParts(parts)}
    </Box>
  )

  return (
    <Typography
      component="div"
      variant="inherit"
      ref={rootRef}
      className="FileNameText-root"
      sx={{ ...ROOT_SX, ...(sx || {}) }}
    >
      {/* The tooltip (and its hover listeners) exists only while the name is shortened. */}
      {truncated ? (
        <Tooltip title={<Box sx={TOOLTIP_TEXT_SX}>{fullName}</Box>} arrow placement="top" enterDelay={300}>
          {textElement}
        </Tooltip>
      ) : (
        textElement
      )}
      <Box component="span" aria-hidden className="FileNameText-sizer" sx={{ ...TEXT_SX, ...variantSx, ...SIZER_SX }}>
        {renderParts(fullParts)}
      </Box>
    </Typography>
  )
})

FileNameText.displayName = 'FileNameText'

FileNameText.propTypes = {
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxLines: PropTypes.number,
  variant: PropTypes.oneOf(['badge', 'plain']),
  sx: PropTypes.object,
}

export default FileNameText
